/**
 * Self-account hooks (task 633).
 *
 * The Account · General page reads and writes the caller's own AMS account —
 * its display name and its URL slug (the `{accountSlug}` segment of every
 * published endpoint URL). Like the profile, this is platform data: it flows
 * AMS → innovate-service proxy → app, and is never read from the JWT. That
 * matters because the token's `account_slug` claim (and the Keycloak SPI's
 * ~30s claims cache) update only on token refresh, so a slug just saved here
 * is authoritative on the server before the token catches up.
 *
 * ── Backend status (reported to the parent integration pass) ────────────────
 * The innovate-service self-account proxy — `GET /api/account` and
 * `PATCH /api/account` — DOES NOT EXIST YET (task 633 steps 1–2). This client
 * is written against that intended contract. Live calls are effectively gated:
 * the page is reachable only once the parent pass wires its route behind the
 * `innovate.fe.settings-account.page` flag, which is off until the backend
 * lands. When the proxy ships, this client can be consolidated into
 * `services/api.ts` as `accountApi` if desired.
 *
 * The axios instance below deliberately mirrors the bearer-token injection of
 * the shared `api` instance in `services/api.ts`; that instance is not
 * exported, and this page-component pass is confined to new files, so the
 * token-attachment is reproduced here rather than imported.
 */

import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import config from '@/config';

const accountHttp = axios.create({
  baseURL: `${config.API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Bearer token, same source as the shared api instance (set + refreshed by
// AuthContext into sessionStorage).
accountHttp.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('kc_token');
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

/**
 * The caller's own account, as the intended `GET /api/account` returns it.
 *   - `id`   the AMS account UUID (shown in the header, copyable for support)
 *   - `name` the display name (in the app and on invoices)
 *   - `slug` the URL slug — the `{accountSlug}` segment of published URLs
 */
export interface Account {
  id: string;
  name: string;
  slug: string;
}

/**
 * Partial account update. Only the two editable fields are sendable; an
 * omitted key is left untouched. The slug is validated server-side for format
 * and uniqueness (a rejection returns 422 with per-field `validationErrors`).
 */
export interface AccountUpdate {
  name?: string;
  slug?: string;
}

interface Wrapped<T> {
  success: boolean;
  data: T;
}

export const accountApi = {
  /** GET /api/account — the caller's own account (intended contract). */
  get: () => accountHttp.get<Wrapped<Account>>('/account'),
  /** PATCH /api/account — partial update, answering with the stored account. */
  update: (update: AccountUpdate) =>
    accountHttp.patch<Wrapped<Account>>('/account', update),
};

/** The query key the account-reading surfaces share. */
export const ACCOUNT_QUERY_KEY = ['account', 'self'] as const;

/** The caller's own account. */
export const useAccount = () =>
  useQuery({
    queryKey: ACCOUNT_QUERY_KEY,
    queryFn: () => accountApi.get().then((res) => res.data.data),
    // The name and slug change rarely; a few-minute window avoids re-fetching
    // on every navigation into settings.
    staleTime: 5 * 60_000,
    retry: 1,
  });

/**
 * Applies a partial account update, writing the server's response into the
 * cache so the view re-renders with recorded truth (a slug normalised
 * server-side, for instance) rather than the submitted values.
 */
export const useUpdateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: AccountUpdate) =>
      accountApi.update(update).then((res) => res.data.data),
    onSuccess: (account: Account) => {
      queryClient.setQueryData(ACCOUNT_QUERY_KEY, account);
    },
  });
};

/** One per-field reason AMS rejected an update, as carried on a 422. */
export interface AccountValidationError {
  field: string;
  message: string;
}

/**
 * Pulls the per-field errors out of a failed save so the form can render them
 * next to the inputs. This client uses a plain axios instance, so a 422 body
 * lives at `error.response.data.details.validationErrors`; a reshaped error
 * (details lifted to the top level) is tolerated too.
 */
export const accountValidationErrors = (error: unknown): AccountValidationError[] => {
  const fromResponse = (error as {
    response?: { data?: { details?: { validationErrors?: AccountValidationError[] } } };
  })?.response?.data?.details;
  const fromTop = (error as {
    details?: { validationErrors?: AccountValidationError[] };
  })?.details;
  const details = fromResponse ?? fromTop;
  return Array.isArray(details?.validationErrors) ? details!.validationErrors! : [];
};
